import { ArrowDownRight } from "lucide-react";
import { Link } from "react-router-dom";
import { photoUrl } from "@/lib/api";
import selection from "@/data/photoSelection.json";
import { HERO } from "@/constants/testIds";

export const Hero = () => (
  <section
    data-testid={HERO.root}
    className="relative h-[100svh] min-h-[680px] w-full overflow-hidden bg-[hsl(var(--ink))]"
  >
    {/* Ken Burns layer */}
    <div className="absolute inset-0 hero-ken-burns">
      <img
        src={photoUrl(selection.hero)}
        alt="Ata House — white gabled exterior with pool and timber deck, Rarotonga"
        className="absolute inset-0 w-full h-full object-cover"
        loading="eager"
        fetchPriority="high"
      />
    </div>

    {/* Layered cinematic gradient */}
    <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/15 to-black/70" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_30%,rgba(0,0,0,0)_0%,rgba(0,0,0,0.35)_85%)]" />

    {/* Top-left logotype that crossfades with nav-as-needed; here purely decorative */}
    <div className="absolute top-0 left-0 right-0 h-px bg-white/15 z-[1]" />

    {/* Content */}
    <div className="relative z-10 h-full container-editorial flex flex-col justify-end pb-32 md:pb-28">
      <div className="max-w-3xl">
        <div className="flex items-center gap-4 mb-8">
          <span className="h-px w-10 bg-white/55" />
          <span className="tracking-[0.42em] text-[10px] md:text-[11px] uppercase text-white/85">
            Muri Beach · Rarotonga · Cook Islands
          </span>
        </div>

        <h1 className="hero-title text-white">
          <span className="block">Kia Orana<span className="text-white/70"> &amp;</span></span>
          <span className="block">
            Welcome to{" "}
            <span className="hero-italic">Ata&nbsp;House</span>
          </span>
        </h1>

        <p className="mt-8 max-w-xl text-white/85 text-base md:text-[17px] font-light leading-relaxed">
          A modern 5-bedroom island home in the heart of Muri Beach — built on the land
          of our ancestors and shared with you as a place to slow down, relax and
          experience Rarotonga in comfort and style.
        </p>

        <div className="mt-8 hidden md:flex flex-wrap items-center gap-x-7 gap-y-2 text-[11px] tracking-[0.28em] uppercase text-white/80">
          <span>5 Bedrooms</span>
          <span className="opacity-50">·</span>
          <span>3 Bathrooms</span>
          <span className="opacity-50">·</span>
          <span>Sleeps 10</span>
          <span className="opacity-50">·</span>
          <span>8m Pool</span>
          <span className="opacity-50">·</span>
          <span>5 Night Minimum</span>
        </div>

        <div className="mt-12 flex flex-wrap gap-4">
          <Link
            to="/book"
            data-testid={HERO.cta}
            className="group inline-flex items-center gap-3 px-7 py-3.5 rounded-full bg-white text-[hsl(var(--ink))] text-[11px] tracking-[0.32em] uppercase font-medium hover:bg-[hsl(var(--linen))] transition-colors duration-500"
          >
            Check Availability &amp; Book
            <ArrowDownRight size={15} strokeWidth={1.5} className="transition-transform duration-500 group-hover:translate-x-0.5 group-hover:translate-y-0.5" />
          </Link>
          <Link
            to="/the-house"
            className="inline-flex items-center gap-3 px-7 py-3.5 rounded-full border border-white/40 text-white text-[11px] tracking-[0.32em] uppercase hover:bg-white/10 hover:border-white/70 transition-all duration-500 backdrop-blur-[2px]"
          >
            Explore the House
          </Link>
        </div>
      </div>
    </div>

    {/* Refined scroll indicator. bottom offset respects the iOS home-bar safe
        area so it isn't clipped on notched phones. */}
    <div
      className="absolute left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-3 pointer-events-none"
      style={{ bottom: "calc(1.75rem + env(safe-area-inset-bottom, 0px))" }}
    >
      <span className="text-white/65 text-[9px] tracking-[0.5em] uppercase">Scroll</span>
      <span className="block w-px h-8 bg-white/40 scroll-pulse" />
    </div>

    <style>{`
      .hero-title {
        font-family: 'Jost', sans-serif;
        font-weight: 200;
        letter-spacing: -0.012em;
        line-height: 1.02;
        font-size: clamp(2.6rem, 6.2vw, 5.6rem);
      }
      .hero-italic {
        font-family: 'Cormorant Garamond', serif;
        font-style: italic;
        font-weight: 400;
        letter-spacing: -0.005em;
      }
      .hero-ken-burns {
        animation: kenburns 28s ease-out forwards;
        transform-origin: center 60%;
      }
      @keyframes kenburns {
        0%   { transform: scale(1.08) translate3d(0, 0, 0); }
        100% { transform: scale(1.0) translate3d(0, 0, 0); }
      }
      .scroll-pulse {
        animation: scrollpulse 2.4s cubic-bezier(0.4, 0, 0.2, 1) infinite;
        transform-origin: top;
      }
      @keyframes scrollpulse {
        0% { transform: scaleY(0); opacity: 0.2; }
        45% { transform: scaleY(1); opacity: 1; }
        100% { transform: scaleY(0); transform-origin: bottom; opacity: 0.2; }
      }
    `}</style>
  </section>
);
